import React, { useState, useEffect } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import {
  Box,
  Container,
  Typography,
  Paper,
  Grid,
  Button,
  Divider,
  CircularProgress,
  Alert,
  Card,
  CardContent,
} from "@mui/material";
import CheckCircleOutlineIcon from "@mui/icons-material/CheckCircleOutline";
import { green } from "@mui/material/colors";
import { orderService } from "../services/orderService";

/**
 * Order confirmation page displayed after successful checkout
 * @returns {JSX.Element} OrderConfirmationPage component
 */
const OrderConfirmationPage = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const queryParams = new URLSearchParams(location.search);
  const orderNumber = queryParams.get("order");

  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [order, setOrder] = useState(null);

  useEffect(() => {
    // Redirect if no order number is provided
    if (!orderNumber) {
      navigate("/");
      return;
    }

    // Fetch order details
    const fetchOrderDetails = async () => {
      try {
        setLoading(true);
        const orderData = await orderService.getOrderByNumber(orderNumber);
        setOrder(orderData);
      } catch (err) {
        console.error("Error fetching order details:", err);
        setError(
          "Failed to load order details. Please contact customer support."
        );
      } finally {
        setLoading(false);
      }
    };

    fetchOrderDetails();
  }, [orderNumber, navigate]);

  // Continue shopping handler
  const handleContinueShopping = () => {
    navigate("/products");
  };

  // View order details handler
  const handleViewOrderDetails = () => {
    navigate(`/account/orders/${order.id}`);
  };

  if (loading) {
    return (
      <Container maxWidth="md" sx={{ mt: 8, mb: 8, textAlign: "center" }}>
        <CircularProgress />
        <Typography variant="body1" sx={{ mt: 2 }}>
          Loading order details...
        </Typography>
      </Container>
    );
  }

  if (error) {
    return (
      <Container maxWidth="md" sx={{ mt: 8, mb: 8 }}>
        <Alert severity="error" sx={{ mb: 4 }}>
          {error}
        </Alert>
        <Button variant="contained" onClick={handleContinueShopping}>
          Continue Shopping
        </Button>
      </Container>
    );
  }

  return (
    <Container maxWidth="md" sx={{ mt: 8, mb: 8 }}>
      <Paper elevation={3} sx={{ p: 4 }}>
        <Box sx={{ textAlign: "center", mb: 4 }}>
          <CheckCircleOutlineIcon sx={{ fontSize: 64, color: green[500] }} />
          <Typography variant="h4" component="h1" gutterBottom>
            Thank You for Your Order!
          </Typography>
          <Typography variant="body1" color="text.secondary">
            Your order has been placed successfully and is being processed.
          </Typography>
        </Box>

        <Card variant="outlined" sx={{ mb: 4 }}>
          <CardContent>
            <Typography variant="h6" gutterBottom>
              Order Information
            </Typography>
            <Grid container spacing={2}>
              <Grid item xs={12} sm={6}>
                <Typography variant="body2" color="text.secondary">
                  Order Number:
                </Typography>
                <Typography variant="body1" gutterBottom>
                  {order?.orderNumber}
                </Typography>
              </Grid>
              <Grid item xs={12} sm={6}>
                <Typography variant="body2" color="text.secondary">
                  Order Date:
                </Typography>
                <Typography variant="body1" gutterBottom>
                  {new Date(order?.createdAt).toLocaleDateString()}
                </Typography>
              </Grid>
              <Grid item xs={12} sm={6}>
                <Typography variant="body2" color="text.secondary">
                  Payment Method:
                </Typography>
                <Typography
                  variant="body1"
                  gutterBottom
                  sx={{ textTransform: "capitalize" }}
                >
                  {order?.paymentMethod?.replace("_", " ")}
                </Typography>
              </Grid>
              <Grid item xs={12} sm={6}>
                <Typography variant="body2" color="text.secondary">
                  Order Status:
                </Typography>
                <Typography
                  variant="body1"
                  gutterBottom
                  sx={{ textTransform: "capitalize" }}
                >
                  {order?.status}
                </Typography>
              </Grid>
            </Grid>
          </CardContent>
        </Card>

        <Card variant="outlined" sx={{ mb: 4 }}>
          <CardContent>
            <Typography variant="h6" gutterBottom>
              Shipping Information
            </Typography>
            <Typography variant="body1">{order?.shippingAddress}</Typography>
          </CardContent>
        </Card>

        <Card variant="outlined" sx={{ mb: 4 }}>
          <CardContent>
            <Typography variant="h6" gutterBottom>
              Order Summary
            </Typography>

            {order?.items?.map((item) => (
              <Box
                key={item.id}
                sx={{ display: "flex", justifyContent: "space-between", mb: 2 }}
              >
                <Typography variant="body1">
                  {item.product?.name} x{item.quantity}
                </Typography>
                <Typography variant="body1">
                  {(parseFloat(item.price) * item.quantity).toFixed(2)} CHF
                </Typography>
              </Box>
            ))}

            <Divider sx={{ my: 2 }} />

            <Box
              sx={{ display: "flex", justifyContent: "space-between", mb: 1 }}
            >
              <Typography variant="body2">Subtotal:</Typography>
              <Typography variant="body2">
                {order?.subtotal.toFixed(2)} CHF
              </Typography>
            </Box>

            <Box
              sx={{ display: "flex", justifyContent: "space-between", mb: 1 }}
            >
              <Typography variant="body2">Shipping:</Typography>
              <Typography variant="body2">
                {order?.shipping > 0
                  ? `${order.shipping.toFixed(2)} CHF`
                  : "Free"}
              </Typography>
            </Box>

            {order?.discount > 0 && (
              <Box
                sx={{ display: "flex", justifyContent: "space-between", mb: 1 }}
              >
                <Typography variant="body2">Discount:</Typography>
                <Typography variant="body2" color="success.main">
                  -{order.discount.toFixed(2)} CHF
                </Typography>
              </Box>
            )}

            <Box
              sx={{ display: "flex", justifyContent: "space-between", mb: 1 }}
            >
              <Typography variant="body2">Tax:</Typography>
              <Typography variant="body2">
                {order?.tax.toFixed(2)} CHF
              </Typography>
            </Box>

            <Box
              sx={{ display: "flex", justifyContent: "space-between", mt: 2 }}
            >
              <Typography variant="h6">Total:</Typography>
              <Typography variant="h6">
                {order?.total.toFixed(2)} CHF
              </Typography>
            </Box>
          </CardContent>
        </Card>

        {order?.paymentMethod === "bank_transfer" && (
          <Card variant="outlined" sx={{ mb: 4, bgcolor: "info.lightest" }}>
            <CardContent>
              <Typography variant="h6" gutterBottom>
                Payment Instructions
              </Typography>
              <Typography variant="body1" paragraph>
                Please transfer the total amount to the following bank account:
              </Typography>
              <Typography variant="body2">
                Bank: Swiss National Bank
                <br />
                IBAN: CH93 0076 2011 6238 5295 7<br />
                BIC: SNBZCHZZXXX
                <br />
                Account Holder: Your B2B Store
                <br />
                Reference: {order.orderNumber}
              </Typography>
            </CardContent>
          </Card>
        )}

        <Box sx={{ display: "flex", justifyContent: "space-between", mt: 4 }}>
          <Button variant="outlined" onClick={handleContinueShopping}>
            Continue Shopping
          </Button>
          <Button variant="contained" onClick={handleViewOrderDetails}>
            View Order Details
          </Button>
        </Box>
      </Paper>
    </Container>
  );
};

export default OrderConfirmationPage;
